import { apiFetch, apiFetchBlob, apiUploadFile } from '@/lib/api-client';
import type {
  RenumberingApplyResult, RenumberingBatchDetail, RenumberingBatchDto, RenumberingVerdict,
} from '@uniticket/shared';

// Écran « Renumbering » (H-1) — admins seulement, gardé côté serveur. Une seule de ces routes écrit dans le
// DMP : l'application, et seulement dans la mapping table. Le dépôt range le fichier, la simulation le
// résout et le juge, l'export sort la liste à renvoyer à Proximus.

export type RenumberingFilter = RenumberingVerdict | 'PENDING' | 'APPLY_FAILED';

export async function fetchRenumberingBatches() {
  return apiFetch<{ data: RenumberingBatchDto[] }>('/renumbering/batches');
}

export async function fetchRenumberingBatch(id: string, filter?: RenumberingFilter) {
  return apiFetch<{ data: RenumberingBatchDetail }>(
    `/renumbering/batches/${id}${filter ? `?verdict=${filter}` : ''}`,
  );
}

/** Gzip dans le navigateur, comme les fichiers Build : même chemin d'upload, même plafond côté serveur,
 *  et un navigateur sans CompressionStream envoie le fichier tel quel — le serveur renifle les octets. */
async function compress(file: File): Promise<Blob> {
  if (typeof CompressionStream === 'undefined') return file;
  return await new Response(file.stream().pipeThrough(new CompressionStream('gzip'))).blob();
}

export async function uploadRenumberingBatch(file: File) {
  return apiUploadFile<{ data: RenumberingBatchDto }>(
    `/renumbering/batches?filename=${encodeURIComponent(file.name)}`,
    await compress(file),
  );
}

export async function simulateRenumberingBatch(id: string) {
  return apiFetch<{ data: RenumberingBatchDetail }>(`/renumbering/batches/${id}/simulate`, { method: 'POST' });
}

/** `simulatedAt` = la simulation que l'écran affiche : le serveur refuse d'appliquer un autre rapport. */
export async function applyRenumberingBatch(id: string, simulatedAt: string) {
  return apiFetch<{ data: RenumberingApplyResult }>(`/renumbering/batches/${id}/apply`, {
    method: 'POST',
    body: JSON.stringify({ simulatedAt }),
  });
}

export async function downloadRenumberingExport(id: string, filename: string) {
  const blob = await apiFetchBlob(`/renumbering/batches/${id}/export.csv`);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
